import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import BackToAdminButton from "../components/BackToAdminButton";
import {
  Package, ChefHat, CheckCircle2, Truck, MapPin, Phone, LogOut,
  RefreshCw, DollarSign, Clock, ThumbsUp, ThumbsDown, Hourglass, Star, Wallet, Car, Undo2, Minus, Plus,
  Navigation, Map as MapIcon, Volume2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import JumlatyLogo from "../components/JumlatyLogo";
import { notify, driverStatusVoice } from "../utils/voice";
import { confirmDialog } from "../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

// ─── Leaflet custom icons (مكرّر من OrderTracking لكن مستقل) ───
const customerIcon = L.divIcon({
  className: "",
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  html: `<div style="position:relative;text-align:center;">
    <div style="background:#E85D45;color:white;border-radius:50%;width:34px;height:34px;display:flex;align-items:center;justify-content:center;margin:0 auto;border:3px solid white;box-shadow:0 4px 8px rgba(0,0,0,0.35);">
      <span style="font-size:16px;">🏪</span>
    </div></div>`,
});
const driverIcon = L.divIcon({
  className: "",
  iconSize: [40, 40],
  iconAnchor: [20, 20],
  html: `<div style="font-size:30px;line-height:1;filter:drop-shadow(0 4px 6px rgba(0,0,0,0.4));">🚚</div>`,
});

// Haversine distance in meters between two lat/lng pairs.
const distanceMeters = (a, b) => {
  if (!a || !b) return null;
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const aa = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(aa)));
};

// Open the customer's coordinates directly inside the Waze app (or web).
const openWaze = (lat, lng) => {
  if (lat == null || lng == null) {
    toast.error("لا يوجد إحداثيات للزبون بعد");
    return;
  }
  const url = `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`;
  window.open(url, "_blank");
};

// Speak an Arabic announcement using the browser SpeechSynthesis API.
const speakAr = (txt) => {
  try {
    if (!window.speechSynthesis) return;
    const u = new SpeechSynthesisUtterance(txt);
    u.lang = "ar-IQ";
    u.rate = 0.95;
    window.speechSynthesis.speak(u);
  } catch { /* ignore */ }
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const statusOptions = [
  { key: "preparing", label: "جاري تجهيز طلبك", color: "#F5A623", icon: ChefHat },
  { key: "ready", label: "تم تجهيز طلبك", color: "#4A90E2", icon: CheckCircle2 },
  { key: "on_the_way", label: "طلبك في طريقه إليك", color: "#7B68EE", icon: Truck },
];

const statusLabels = {
  assigned: { label: "طلب جديد لك", color: "#E85D45" },
  accepted: { label: "تم القبول", color: "#7ED321" },
  preparing: { label: "جاري التجهيز", color: "#F5A623" },
  ready: { label: "تم التجهيز", color: "#4A90E2" },
  on_the_way: { label: "في الطريق", color: "#7B68EE" },
  awaiting_payment_confirmation: { label: "بانتظار تأكيد الإدارة", color: "#F5A623" },
  delivered: { label: "تم التوصيل", color: "#7ED321" },
};

const ReturnModal = ({ order, onClose, onDone }) => {
  const [qtys, setQtys] = useState(() => Object.fromEntries((order.items || []).map((it) => [it.product_id, 0])));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const total = (order.items || []).reduce((sum, it) => sum + (qtys[it.product_id] || 0) * it.price, 0);
  const totalQty = (order.items || []).reduce((sum, it) => sum + (qtys[it.product_id] || 0), 0);

  const adjust = (id, delta, max) => {
    setQtys((q) => ({ ...q, [id]: Math.max(0, Math.min(max, (q[id] || 0) + delta)) }));
  };

  const submit = async () => {
    const items = (order.items || [])
      .filter((it) => (qtys[it.product_id] || 0) > 0)
      .map((it) => ({
        product_id: it.product_id,
        name_ar: it.name_ar || it.name,
        quantity: qtys[it.product_id],
        price: it.price,
        cost_price: it.cost_price || 0,
      }));
    if (items.length === 0) { toast.error("اختر مادة واحدة على الأقل"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/driver/orders/${order.id}/return`, { items, reason });
      toast.success("تم إرسال طلب الإرجاع للإدارة");
      onDone();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "خطأ");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4" data-testid="return-modal">
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto p-5">
        <h3 className="font-heading font-bold text-xl text-[#111814] mb-3 flex items-center gap-2">
          <Undo2 className="w-5 h-5 text-[#E85D45]" />تعديل الفاتورة — فاتورة الإرجاع
        </h3>
        <p className="text-xs text-[#5C6E64] mb-3">حدّد كم كرتون يرجع الزبون من كل مادة (ستُرسل تلقائياً للإدارة للموافقة):</p>

        <div className="space-y-2 mb-4">
          {(order.items || []).map((it) => (
            <div key={it.product_id} className="bg-[#FAFCFA] border border-[#E4EBE6] rounded-xl p-3" data-testid={`return-item-${it.product_id}`}>
              <div className="flex justify-between items-center mb-2">
                <p className="font-bold text-sm">{it.name_ar || it.name}</p>
                <p className="text-xs text-[#5C6E64]">طُلب: {it.quantity}</p>
              </div>
              <div className="flex items-center justify-between">
                <button type="button" onClick={() => adjust(it.product_id, -1, it.quantity)}
                  data-testid={`ret-minus-${it.product_id}`}
                  className="w-8 h-8 rounded-full bg-[#E85D45] text-white flex items-center justify-center disabled:opacity-30"
                  disabled={(qtys[it.product_id] || 0) === 0}>
                  <Minus className="w-4 h-4" />
                </button>
                <span className="font-bold text-2xl" data-testid={`ret-qty-${it.product_id}`}>{qtys[it.product_id] || 0}</span>
                <button type="button" onClick={() => adjust(it.product_id, 1, it.quantity)}
                  data-testid={`ret-plus-${it.product_id}`}
                  className="w-8 h-8 rounded-full bg-[#7ED321] text-white flex items-center justify-center disabled:opacity-30"
                  disabled={(qtys[it.product_id] || 0) >= it.quantity}>
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <textarea value={reason} onChange={(e) => setReason(e.target.value)}
          data-testid="return-reason"
          placeholder="سبب الإرجاع (اختياري)..." rows={2}
          className="w-full px-4 py-2 rounded-2xl border border-[#E4EBE6] bg-white text-sm resize-none mb-3" />

        <div className="bg-[#FFF8E1] rounded-xl p-3 mb-3 text-sm">
          <div className="flex justify-between"><span>عدد الكراتين المرتجعة:</span><b>{totalQty}</b></div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button onClick={onClose} disabled={busy}
            className="bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] font-bold py-2.5 rounded-full">
            إلغاء
          </button>
          <button onClick={submit} disabled={busy || totalQty === 0} data-testid="submit-return"
            className="bg-[#E85D45] hover:bg-[#c14a36] text-white font-bold py-2.5 rounded-full disabled:opacity-50">
            {busy ? "جاري الإرسال..." : "إرسال للإدارة"}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// ─── Driver-side mini map for an order (live customer + driver positions + Waze) ───
const DriverOrderMap = ({ orderId, myPos }) => {
  const [tracking, setTracking] = useState(null);
  const announcedRef = useRef(false);

  const fetchTracking = async () => {
    try {
      const { data } = await axios.get(`${API}/orders/${orderId}/tracking`);
      setTracking(data);
    } catch { /* non-fatal */ }
  };

  useEffect(() => {
    fetchTracking();
    const id = setInterval(fetchTracking, 10000); // refresh customer/driver positions every 10s
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [orderId]);

  const cLat = tracking?.customer?.lat;
  const cLng = tracking?.customer?.lng;
  const customerPos = (cLat != null && cLng != null) ? { lat: cLat, lng: cLng } : null;
  // Use live GPS (myPos) if available, else fallback to backend's last reported driver pos
  const driverPos = myPos || (
    tracking?.driver?.lat != null && tracking?.driver?.lng != null
      ? { lat: tracking.driver.lat, lng: tracking.driver.lng } : null
  );

  const dist = customerPos && driverPos ? distanceMeters(driverPos, customerPos) : null;

  // Announce when we get within 150m of the customer (once per mount)
  useEffect(() => {
    if (dist != null && dist <= 150 && !announcedRef.current) {
      announcedRef.current = true;
      speakAr("اقتربتَ من الزبون، المسافة أقل من 150 متراً");
    }
  }, [dist]);

  if (!customerPos) {
    return (
      <div className="bg-[#FFF8E1] border-2 border-[#F5A623]/40 rounded-2xl p-3 text-center" data-testid={`map-nocoord-${orderId}`}>
        <AlertTriangle className="w-5 h-5 text-[#F5A623] inline-block mb-1" />
        <p className="text-xs text-[#5C6E64]">لم يحدّد الزبون موقعه على الخريطة بعد</p>
      </div>
    );
  }

  const center = driverPos || customerPos;
  const positions = [customerPos];
  if (driverPos) positions.push(driverPos);
  const bounds = positions.length > 1 ? positions.map((p) => [p.lat, p.lng]) : null;

  return (
    <div className="relative rounded-2xl overflow-hidden border-2 border-[#3B82F6]/50 shadow-sm" data-testid={`order-map-${orderId}`}>
      <MapContainer
        center={[center.lat, center.lng]}
        zoom={14}
        bounds={bounds}
        boundsOptions={{ padding: [40, 40] }}
        style={{ height: "220px", width: "100%", pointerEvents: "none" }}
        scrollWheelZoom={false}
        dragging={false}
        doubleClickZoom={false}
        touchZoom={false}
        boxZoom={false}
        keyboard={false}
        tap={false}
        zoomControl={false}
        attributionControl={false}
      >
        <TileLayer
          attribution='&copy; OpenStreetMap'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker position={[customerPos.lat, customerPos.lng]} icon={customerIcon} />
        {driverPos && <Marker position={[driverPos.lat, driverPos.lng]} icon={driverIcon} />}
        {driverPos && (
          <Polyline
            positions={[[driverPos.lat, driverPos.lng], [customerPos.lat, customerPos.lng]]}
            pathOptions={{ color: "#3B82F6", weight: 5, opacity: 0.85, dashArray: "8 4" }}
          />
        )}
      </MapContainer>
      {/* Floating action bar */}
      <div className="absolute top-2 left-2 right-2 flex justify-between gap-2 pointer-events-none z-[400]">
        {dist != null && (
          <span className="bg-white/95 text-[#1E3A5F] text-xs font-black rounded-full px-2.5 py-1 shadow pointer-events-auto" dir="ltr"
            data-testid={`distance-${orderId}`}>
            {dist >= 1000 ? `${(dist / 1000).toFixed(1)} km` : `${dist} m`}
          </span>
        )}
        <div className="flex gap-1 pointer-events-auto mr-auto">
          <button
            type="button"
            onClick={() => openWaze(customerPos.lat, customerPos.lng)}
            data-testid={`waze-btn-${orderId}`}
            title="فتح في تطبيق Waze للملاحة الصوتية"
            className="bg-[#33CCFF] hover:bg-[#1ec0ed] text-white text-[11px] font-black rounded-full px-2.5 py-1 shadow flex items-center gap-1"
          >
            <Navigation className="w-3 h-3" /> Waze
          </button>
          <button
            type="button"
            onClick={() => {
              const url = `https://www.google.com/maps/dir/?api=1&destination=${customerPos.lat},${customerPos.lng}&travelmode=driving`;
              window.open(url, "_blank");
            }}
            data-testid={`gmaps-btn-${orderId}`}
            title="فتح Google Maps للاتجاهات"
            className="bg-[#4285F4] hover:bg-[#3367d6] text-white text-[11px] font-black rounded-full px-2.5 py-1 shadow flex items-center gap-1"
          >
            <MapIcon className="w-3 h-3" /> Maps
          </button>
        </div>
      </div>
    </div>
  );
};

const PaymentForm = ({ order, onSubmitted }) => {
  // Simplified driver flow: driver does NOT touch money anymore.
  // Driver only confirms delivery (with optional note). Admin clears
  // the customer's debt manually from the office.
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!(await confirmDialog("تأكيد توصيل الطلب للزبون؟\nسيرسل الطلب للإدارة لمعالجة الفاتورة."))) return;
    setBusy(true);
    try {
      await axios.patch(`${API}/driver/orders/${order.id}/submit-payment`, {
        amount_paid: 0,
        note: note ? `[توصيل] ${note}` : "[توصيل]",
      });
      toast.success("تم تأكيد التوصيل — الإدارة ستعالج الفاتورة");
      onSubmitted();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل الإرسال");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-[#F0F9FF] border border-[#4A90E2]/30 rounded-2xl p-4 mt-3 space-y-3">
      <h4 className="font-bold text-[#111814] flex items-center gap-2">
        <CheckCircle2 className="w-4 h-4 text-[#7ED321]" />
        تأكيد توصيل الطلب
      </h4>
      <div className="bg-white rounded-xl p-3 space-y-1 border border-[#E4EBE6]">
        <p className="text-xs text-[#5C6E64]">الزبون: <span className="font-bold text-[#111814]">{order.user_name}</span></p>
        <p className="text-xs text-[#5C6E64]">فاتورة رقم: <span className="font-bold text-[#111814]">#{order.invoice_number || order.id.slice(0, 8)}</span></p>
        <p className="text-xs text-[#5C6E64]">الإجمالي: <span className="font-bold text-[#E85D45]" dir="ltr">{fmt(order.total)} د.ع</span></p>
      </div>
      <p className="text-[11px] text-[#5C6E64] bg-[#FEF3C7] rounded-lg p-2 leading-tight">
        ℹ️ لا حاجة لاستلام مبالغ — الإدارة هي من تعالج الديون والمحاسبة.
      </p>
      <textarea
        rows={2}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="ملاحظة للإدارة (اختياري) — مثلاً: الزبون لم يكن موجوداً، أو ملاحظة عن المنتج..."
        data-testid={`delivery-note-${order.id}`}
        className="w-full px-4 py-2 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#4A90E2] bg-white text-sm"
      />
      <button type="submit" disabled={busy} data-testid={`submit-delivery-${order.id}`}
        className="w-full bg-gradient-to-l from-[#7ED321] to-[#5fa617] hover:from-[#6cb91d] text-white font-bold py-3 rounded-full disabled:opacity-50">
        {busy ? "جاري الإرسال..." : "✓ تم توصيل الطلب"}
      </button>
    </form>
  );
};

const OrderCard = ({ order, onRefresh, myPos }) => {
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showReturn, setShowReturn] = useState(false);
  const [busy, setBusy] = useState(false);

  const callApi = async (path, label) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/driver/orders/${order.id}/${path}`);
      toast.success(label);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل");
    } finally { setBusy(false); }
  };

  const handleStatus = async (status) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/driver/orders/${order.id}/status`, { status });
      toast.success("تم تحديث الحالة");
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل");
    } finally { setBusy(false); }
  };

  const sLabel = statusLabels[order.status] || { label: order.status, color: "#5C6E64" };
  const isAssigned = order.status === "assigned";
  const isAccepted = ["accepted", "preparing", "ready", "on_the_way"].includes(order.status);
  const isAwaiting = order.status === "awaiting_payment_confirmation";
  const isDelivered = order.status === "delivered";

  // Return status from backend (pending blocks submit-payment until admin decides)
  const pendingReturn = order.pending_return || null;
  const returnsHistory = order.returns_history || [];
  const lastDecidedReturn = returnsHistory.find((r) => r.status === "approved" || r.status === "rejected");

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      data-testid={`driver-order-${order.id}`}
      className={`bg-white border rounded-2xl p-4 shadow-sm space-y-3 ${
        isAssigned ? "border-[#E85D45] ring-2 ring-[#E85D45]/30" : "border-[#BAE6FD]"
      }`}
    >
      <div className="flex justify-between items-start gap-2 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-bold text-[#111814]">{order.user_name}</p>
            {order.customer_id && (
              <span className="text-xs bg-[#E0F2FE] px-2 py-0.5 rounded-full font-mono font-bold text-[#2D4A3E]">
                ايباد: {order.customer_id}
              </span>
            )}
          </div>
          <a href={`tel:${order.user_phone}`} className="text-sm text-[#E85D45] flex items-center gap-1 hover:underline mt-1">
            <Phone className="w-3 h-3" />{order.user_phone}
          </a>
          <p className="text-xs text-[#5C6E64] mt-1">{formatDate(order.created_at)}</p>
        </div>
        <span className="px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap"
          style={{ backgroundColor: `${sLabel.color}20`, color: sLabel.color }}>
          {sLabel.label}
        </span>
      </div>

      <div className="bg-[#FAFCFA] rounded-xl p-3 flex items-start gap-2">
        <MapPin className="w-4 h-4 text-[#E85D45] mt-0.5 flex-shrink-0" />
        <p className="text-sm text-[#111814]">{order.address || "بدون عنوان"}</p>
      </div>

      <div className="space-y-1.5">
        {order.items?.map((item, idx) => (
          <div key={idx} className="flex justify-between text-sm">
            <span className="text-[#111814]">{item.name_ar}</span>
            <span className="font-medium tabular-nums">× {item.quantity}</span>
          </div>
        ))}
        {/* Driver no longer sees the money side — only the carton count so
            they know what they're carrying. Per business rules, the driver
            must never see or handle the customer's debt or order total. */}
        <div className="flex justify-between font-bold border-t border-[#E4EBE6] pt-2 mt-2 text-[#2D4A3E]">
          <span>إجمالي الكراتين</span>
          <span dir="ltr" className="text-lg">
            {fmt(order.items?.reduce((s, it) => s + (it.quantity || 0), 0))} كرتون
          </span>
        </div>
      </div>

      {order.notes && (
        <div className="bg-[#FFF8E1] border border-[#F5A623]/30 rounded-xl px-3 py-2 text-sm">
          <span className="text-xs font-bold text-[#5C6E64]">ملاحظة الزبون: </span>{order.notes}
        </div>
      )}

      {/* ─── State-specific UI ─── */}
      {isAssigned && (
        <div className="space-y-2">
          <div className="bg-[#FFE8E5] border border-[#E85D45]/30 rounded-xl px-3 py-2 text-sm font-bold text-[#E85D45] text-center">
            🔔 طلب جديد بانتظار قبولك
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => callApi("accept", "تم قبول الطلب")} disabled={busy}
              data-testid={`accept-${order.id}`}
              className="bg-[#7ED321] hover:bg-[#6cb91d] text-white font-bold py-3 rounded-full flex items-center justify-center gap-2 disabled:opacity-50">
              <ThumbsUp className="w-4 h-4" /> موافقة
            </button>
            <button onClick={async () => {
                if (await confirmDialog("رفض هذا الطلب وإعادته للإدارة؟")) callApi("reject", "تم رفض الطلب");
              }} disabled={busy}
              data-testid={`reject-${order.id}`}
              className="bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] font-bold py-3 rounded-full flex items-center justify-center gap-2 disabled:opacity-50">
              <ThumbsDown className="w-4 h-4" /> رفض
            </button>
          </div>
        </div>
      )}

      {isAccepted && (
        <>
          {/* Live mini-map: customer pin, my location, polyline, Waze button */}
          <DriverOrderMap orderId={order.id} myPos={myPos} />

          {/* Return banners — pending blocks submit-payment, approved/rejected info */}
          {pendingReturn && (
            <div className="bg-[#FFF8E1] border-2 border-[#F5A623]/60 rounded-2xl p-3 text-sm space-y-1" data-testid={`return-pending-${order.id}`}>
              <p className="font-bold text-[#B45309] flex items-center gap-1">
                <Hourglass className="w-4 h-4 animate-pulse" /> طلب إرجاع بانتظار موافقة الإدارة
              </p>
              <p className="text-xs text-[#92400e]" dir="ltr">
                {fmt(pendingReturn.total_qty || 0)} كرتون
              </p>
              {pendingReturn.reason && (
                <p className="text-xs text-[#5C6E64]">السبب: {pendingReturn.reason}</p>
              )}
              <p className="text-[10px] text-[#B45309] mt-1">⚠ زر التوصيل مقفل حتى تبت الإدارة في الطلب</p>
            </div>
          )}
          {!pendingReturn && lastDecidedReturn && lastDecidedReturn.status === "approved" && (
            <div className="bg-[#ECFDF5] border-2 border-[#15803d]/40 rounded-2xl p-3 text-sm" data-testid={`return-approved-${order.id}`}>
              <p className="font-bold text-[#15803d] flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" /> الإدارة وافقت على الإرجاع
              </p>
              <p className="text-xs text-[#166534] mt-1">
                تم خصم المواد المرتجعة من الفاتورة — أعطِ ما تبقى للزبون
              </p>
            </div>
          )}
          {!pendingReturn && lastDecidedReturn && lastDecidedReturn.status === "rejected" && (
            <div className="bg-[#FEF2F2] border-2 border-[#E85D45]/40 rounded-2xl p-3 text-sm" data-testid={`return-rejected-${order.id}`}>
              <p className="font-bold text-[#E85D45] flex items-center gap-1">
                <ThumbsDown className="w-4 h-4" /> الإدارة رفضت الإرجاع
              </p>
              <p className="text-xs text-[#5C6E64] mt-1">الإدارة رفضت الإرجاع — أعطِ البضاعة كما هي</p>
            </div>
          )}

          <div className="grid grid-cols-3 gap-2">
            {statusOptions.map((opt) => {
              const Icon = opt.icon;
              const active = order.status === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => handleStatus(opt.key)}
                  disabled={busy || active}
                  data-testid={`status-${opt.key}-${order.id}`}
                  className={`flex flex-col items-center gap-1 p-2.5 rounded-xl text-xs font-bold transition-all border-2 ${
                    active ? "ring-2 ring-offset-1" : "border-[#E4EBE6] hover:bg-[#FAFCFA]"
                  }`}
                  style={active
                    ? { backgroundColor: `${opt.color}15`, borderColor: opt.color, color: opt.color, "--tw-ring-color": opt.color }
                    : { color: "#5C6E64" }}
                >
                  <Icon className="w-4 h-4" />
                  <span className="leading-tight">{opt.label}</span>
                </button>
              );
            })}
          </div>

          {!showPaymentForm ? (
            <>
              <button
                onClick={async () => {
                  if (pendingReturn) {
                    toast.error("يوجد طلب إرجاع قيد المراجعة — انتظر حتى تبت الإدارة");
                    return;
                  }
                  if (!(await confirmDialog("تأكيد توصيل الطلب للزبون؟"))) return;
                  setBusy(true);
                  try {
                    const { data } = await axios.patch(`${API}/driver/orders/${order.id}/mark-delivered`);
                    toast.success("✅ تم التوصيل!");
                    void data;
                    onRefresh();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "فشل التوصيل");
                  } finally {
                    setBusy(false);
                  }
                }}
                disabled={!!pendingReturn || busy}
                data-testid={`mark-delivered-${order.id}`}
                title={pendingReturn ? "زر مقفل: طلب إرجاع قيد المراجعة" : undefined}
                className={`w-full font-bold py-4 rounded-full flex items-center justify-center gap-2 text-white text-lg shadow-lg active:scale-95 transition-transform ${
                  pendingReturn
                    ? "bg-[#9CA3AF] cursor-not-allowed"
                    : "bg-gradient-to-l from-[#7ED321] to-[#5fa617] hover:from-[#6cb91d] hover:to-[#4f8b13]"
                }`}
              >
                <CheckCircle2 className="w-5 h-5" />
                {pendingReturn ? "بانتظار قرار الإدارة..." : "✓ تم التوصيل"}
              </button>
              <button
                onClick={() => setShowReturn(true)}
                disabled={!!pendingReturn}
                data-testid={`open-return-${order.id}`}
                className="w-full mt-2 bg-white border-2 border-[#E85D45] text-[#E85D45] hover:bg-[#FEF2F2] font-bold py-2.5 rounded-full flex items-center justify-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                <Undo2 className="w-4 h-4" />تعديل الفاتورة (إرجاع مواد)
              </button>
            </>
          ) : (
            <PaymentForm order={order} onSubmitted={onRefresh} />
          )}
          {showReturn && <ReturnModal order={order} onClose={() => setShowReturn(false)} onDone={onRefresh} />}
        </>
      )}

      {isAwaiting && (
        <div className="bg-[#FFF8E1] border-2 border-[#F5A623]/40 rounded-2xl p-4 text-center">
          <Hourglass className="w-8 h-8 text-[#F5A623] mx-auto mb-2 animate-pulse" />
          <p className="font-bold text-[#111814]">تم تأكيد التوصيل</p>
          <p className="text-xs text-[#5C6E64] mt-2">الإدارة ستعالج الفاتورة والمحاسبة مع الزبون</p>
        </div>
      )}

      {isDelivered && (
        <div className="bg-[#E8F5E9] border border-[#7ED321]/30 rounded-xl p-3 text-sm text-center">
          <p className="font-bold text-[#2D4A3E]">✅ تم التوصيل بنجاح</p>
        </div>
      )}
    </motion.div>
  );
};

const Driver = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active");
  const [submittingCash, setSubmittingCash] = useState(false);
  const [markingAvail, setMarkingAvail] = useState(false);
  // Live GPS position of the driver — pushed to backend AND used by maps
  const [myPos, setMyPos] = useState(null);
  const prevAssignedCountRef = useRef(0);
  const lastPostedRef = useRef(0);

  const fetchOrders = async () => {
    try {
      const [ordersRes, statsRes, vehRes] = await Promise.all([
        axios.get(`${API}/driver/orders`),
        axios.get(`${API}/driver/me`).catch(() => ({ data: null })),
        axios.get(`${API}/driver/vehicle`).catch(() => ({ data: null })),
      ]);
      const data = ordersRes.data;
      const newAssigned = data.filter((o) => o.status === "assigned").length;
      if (prevAssignedCountRef.current > 0 && newAssigned > prevAssignedCountRef.current) {
        notify(driverStatusVoice.assigned);
      }
      prevAssignedCountRef.current = newAssigned;
      setOrders(data);
      setStats(statsRes.data);
      setVehicle(vehRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("هذه الصفحة للسائقين فقط");
        navigate("/");
      }
    } finally { setLoading(false); }
  };

  const markVehicleAvailable = async () => {
    setMarkingAvail(true);
    try {
      await axios.patch(`${API}/driver/vehicle/available`);
      toast.success("صرت متاحاً للفواتير الجديدة");
      fetchOrders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل");
    } finally { setMarkingAvail(false); }
  };

  const handleSubmitCash = async () => {
    if (!stats || stats.cash_collected <= 0) return;
    if (!(await confirmDialog(`تأكيد تسليم ${stats.cash_collected.toLocaleString("en-US")} د.ع للإدارة؟`))) return;
    setSubmittingCash(true);
    try {
      await axios.post(`${API}/driver/submit-cash`);
      toast.success("تم إرسال طلب التسليم للإدارة");
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل");
    } finally { setSubmittingCash(false); }
  };

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    // Allow admin to view this portal for inspection (no redirect)
    if (user.role !== "driver" && user.role !== "admin") { navigate("/"); return; }
    fetchOrders();
    const interval = setInterval(fetchOrders, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // GPS auto-ping while there's an active order. Uses watchPosition so the
  // map updates fluidly, and throttles POSTs to /driver/location to once
  // every 12s to avoid hammering the backend.
  // ─── 24/7 Live tracking — POLICY (Feb 27, 2026) ───
  // Drivers' GPS now stays on the WHOLE TIME they're logged in, not just
  // when there's an active order. The admin (NOT the driver) flips a hidden
  // toggle (`tracking_enabled` on the user doc); we poll it every 60s. If
  // OFF, we silently stop pushing.
  // For the native APK build, a Capacitor background-geolocation plugin
  // takes over so tracking continues even with the screen off.
  useEffect(() => {
    if (!user || user.role !== "driver") return;

    // Native APK path — runs in addition to (and outlives) the web watcher.
    let nativeTracker;
    (async () => {
      try {
        const mod = await import("../services/driverNativeTracking");
        nativeTracker = mod.default;
        nativeTracker.start();
      } catch { /* native module absent on web — fine */ }
    })();

    if (!navigator.geolocation) return;

    let trackingEnabled = true;
    let stateInterval = null;
    let watchId = null;

    const checkState = async () => {
      try {
        const { data } = await axios.get(`${API}/driver/me/tracking-state`);
        trackingEnabled = !!data?.tracking_enabled;
      } catch { /* keep last known state */ }
    };

    const startWatch = () => {
      if (watchId !== null) return;
      watchId = navigator.geolocation.watchPosition(
        (pos) => {
          const here = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setMyPos(here);
          if (!trackingEnabled) return; // admin disabled — don't push
          const now = Date.now();
          if (now - lastPostedRef.current >= 12000) {
            lastPostedRef.current = now;
            axios.post(`${API}/driver/location`, here).catch(() => {});
          }
        },
        () => { /* permission denied — silent */ },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
      );
    };

    // Boot: fetch tracking state once, start watcher, then re-check state
    // every 60s so admin toggles propagate without a refresh.
    checkState().then(startWatch);
    stateInterval = setInterval(checkState, 60000);

    return () => {
      if (stateInterval) clearInterval(stateInterval);
      if (watchId !== null) {
        try { navigator.geolocation.clearWatch(watchId); } catch { /* ignore */ }
      }
      if (nativeTracker) {
        try { nativeTracker.stop(); } catch { /* ignore */ }
      }
    };
  }, [user]);

  const newOrders = orders.filter((o) => o.status === "assigned");
  const active = orders.filter((o) => !["delivered"].includes(o.status));
  const delivered = orders.filter((o) => o.status === "delivered");
  const shown = filter === "active" ? active : delivered;

  return (
    <div className="min-h-screen bg-transparent">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/60 border-b border-[#BAE6FD]/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
          <div className="flex flex-col items-start">
            <JumlatyLogo size="sm" showTagline={false} />
            <span className="text-xs text-[#3B82F6] font-bold mt-1">سائقون جملتي</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchOrders} data-testid="refresh-btn"
              className="p-2 hover:bg-[#E0F2FE] rounded-full" title="تحديث">
              <RefreshCw className="w-5 h-5 text-[#2D4A3E]" />
            </button>
            <button onClick={() => { logout(); navigate("/login"); }} data-testid="logout-btn"
              className="flex items-center gap-2 px-3 py-2 hover:bg-[#FFE8E5] rounded-full text-[#E85D45] text-sm">
              <LogOut className="w-4 h-4" />
              تسجيل الخروج
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        <div>
          <h1 className="font-heading font-bold text-2xl text-[#111814]">
            <Truck className="w-7 h-7 inline ml-2 text-[#4A90E2]" />
            لوحة السائق
          </h1>
          <p className="text-sm text-[#5C6E64] mt-1">مرحباً {user?.full_name}</p>
        </div>

        {newOrders.length > 0 && (
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            data-testid="new-orders-banner"
            className="bg-gradient-to-l from-[#E85D45] to-[#c14a36] text-white rounded-2xl px-4 py-3 flex items-center gap-3 shadow-lg animate-pulse"
          >
            <span className="text-2xl">🔔</span>
            <p className="font-bold">
              لديك <span className="text-2xl">{newOrders.length}</span> طلب جديد بانتظار موافقتك
            </p>
          </motion.div>
        )}

        {/* Vehicle availability card */}
        {vehicle && (
          <div data-testid="driver-vehicle-card"
            className={`rounded-2xl p-3 border-2 flex items-center justify-between gap-2 shadow-sm ${
              vehicle.status === "busy" ? "bg-[#FEF2F2] border-[#FCA5A5]" : "bg-[#F0FDF4] border-[#86efac]"
            }`}>
            <div className="flex items-center gap-2 min-w-0">
              <Truck className={`w-6 h-6 ${vehicle.status === "busy" ? "text-[#E85D45]" : "text-[#15803d]"}`} />
              <div className="min-w-0">
                <p className="font-bold text-sm truncate">{vehicle.name}{vehicle.plate ? ` — ${vehicle.plate}` : ""}</p>
                <p className="text-[11px] text-[#5C6E64]">
                  حالة المركبة: <span className={`font-bold ${vehicle.status === "busy" ? "text-[#E85D45]" : "text-[#15803d]"}`}>{vehicle.status === "busy" ? "غير متاح" : "متاح الآن"}</span>
                  {Number(vehicle.active_orders || 0) > 0 && <> · فواتير نشطة: <span dir="ltr" className="font-bold">{vehicle.active_orders}</span></>}
                </p>
              </div>
            </div>
            {vehicle.status === "busy" && Number(vehicle.active_orders || 0) === 0 && (
              <button onClick={markVehicleAvailable} disabled={markingAvail} data-testid="driver-mark-available"
                className="bg-[#15803d] hover:bg-[#126b32] disabled:opacity-50 text-white text-xs font-bold py-2 px-3 rounded-full flex items-center gap-1 shrink-0">
                <CheckCircle2 className="w-4 h-4" />
                خلصت — متاح الآن
              </button>
            )}
            {vehicle.status === "busy" && Number(vehicle.active_orders || 0) > 0 && (
              <span className="text-[11px] bg-[#FEE2E2] text-[#E85D45] font-bold px-2 py-1 rounded-full shrink-0">
                أنهِ الفواتير أولاً
              </span>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="bg-white border border-[#BAE6FD] rounded-2xl p-3 text-center">
            <Clock className="w-5 h-5 text-[#F5A623] mx-auto mb-1" />
            <p className="font-bold text-xl text-[#111814]">{stats?.today_delivered ?? 0}</p>
            <p className="text-xs text-[#5C6E64]">طلبات اليوم</p>
          </div>
          <div className="bg-white border border-[#BAE6FD] rounded-2xl p-3 text-center">
            <CheckCircle2 className="w-5 h-5 text-[#7ED321] mx-auto mb-1" />
            <p className="font-bold text-xl text-[#111814]">{stats?.month_delivered ?? 0}</p>
            <p className="text-xs text-[#5C6E64]">هذا الشهر</p>
          </div>
          {/* Bonus/carton stat REMOVED (Feb 17, 2026) — 30 IQD/carton
              commission disabled by admin. */}
          <div className="bg-gradient-to-br from-[#2D4A3E] to-[#1f3329] text-white rounded-2xl p-3 text-center">
            <Star className="w-5 h-5 mx-auto mb-1 opacity-80" />
            <p className="font-bold text-lg">{stats?.avg_rating || "—"}</p>
            <p className="text-xs opacity-90">{stats?.rating_count || 0} تقييم</p>
          </div>
        </div>

        {/* Driver bonus card REMOVED (Feb 17, 2026) — admin disabled the
            30 IQD/carton commission. Card intentionally hidden even if a
            stale balance exists on legacy accounts. */}

        {/* Cash collected card */}
        {stats && stats.cash_collected > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            data-testid="driver-cash-card"
            className="bg-gradient-to-l from-[#E85D45] to-[#c14a36] text-white rounded-2xl p-5"
          >
            <div className="flex items-center gap-2 mb-2 opacity-90">
              <Wallet className="w-5 h-5" />
              <span className="text-sm">المبلغ المُحصَّل بانتظار التسليم</span>
            </div>
            <p className="font-bold text-3xl mb-3">{Number(stats.cash_collected).toLocaleString("en-US")} د.ع</p>
            <button onClick={handleSubmitCash} disabled={submittingCash}
              data-testid="submit-cash-btn"
              className="w-full bg-white text-[#E85D45] font-bold py-3 rounded-full hover:bg-[#FFF5F3] disabled:opacity-50">
              {submittingCash ? "جاري الإرسال..." : "تم تسليم المبلغ للإدارة"}
            </button>
          </motion.div>
        )}

        {/* Driver info */}
        {stats?.vehicle_type && (
          <div className="bg-white border border-[#E4EBE6] rounded-2xl px-4 py-2 text-xs text-[#5C6E64] flex items-center gap-3 flex-wrap">
            <Car className="w-4 h-4 text-[#4A90E2]" />
            <span>{stats.vehicle_type}</span>
            {stats.vehicle_number && <span className="font-bold">رقم: {stats.vehicle_number}</span>}
          </div>
        )}

        <div className="flex gap-2">
          <button onClick={() => setFilter("active")} data-testid="filter-active"
            className={`px-4 py-2 rounded-full text-sm font-medium ${
              filter === "active" ? "bg-[#2D4A3E] text-white" : "bg-white border border-[#E4EBE6] text-[#5C6E64]"
            }`}>
            نشطة ({active.length})
          </button>
          <button onClick={() => setFilter("delivered")} data-testid="filter-delivered"
            className={`px-4 py-2 rounded-full text-sm font-medium ${
              filter === "delivered" ? "bg-[#2D4A3E] text-white" : "bg-white border border-[#E4EBE6] text-[#5C6E64]"
            }`}>
            منجزة ({delivered.length})
          </button>
        </div>

        {loading ? (
          <p className="text-center text-[#5C6E64] py-8">جاري التحميل...</p>
        ) : shown.length === 0 ? (
          <div className="bg-white border border-[#BAE6FD] rounded-2xl py-12 text-center">
            <Package className="w-16 h-16 text-[#E4EBE6] mx-auto mb-3" />
            <p className="text-[#5C6E64]">لا توجد طلبات</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="driver-orders">
            {shown.map((o) => (
              <OrderCard key={o.id} order={o} onRefresh={fetchOrders} myPos={myPos} />
            ))}
          </div>
        )}
      </main>
      <BackToAdminButton />
    </div>
  );
};

export default Driver;
